import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import type { Role } from "@xinghai/shared";
import { AppShell as UiAppShell, Icon, IconButton, Select, useToast } from "@xinghai/ui";
import type { IconId, NavItem, RenderLink } from "@xinghai/ui";
import { useAuth } from "../features/auth/useAuth";
import { usersApi } from "../lib/api";
import { friendlyMessage } from "../lib/apiErrors";
import { qk } from "../lib/queries";
import { useAiEnabled } from "../features/ai/useAiEnabled";
import { AccountMenu } from "./AccountMenu";
import { homeFor, ROLE_LABELS, sortRoles, useActiveRole } from "./roles";
import { THEME_LABELS, useTheme } from "./theme";
import { useOnline } from "./useOnline";
import { useUploadQueueBridge } from "./useUploadQueueBridge";
import { pendingCount, useUploadQueue } from "../lib/queue";

interface NavDef {
  to: string;
  label: string;
  icon: IconId;
  /** `end` matches the route exactly (so /s does not stay active on /s/me). */
  end?: boolean;
}

/**
 * Role-aware navigation. On phones the first four entries become the bottom
 * tab bar, so the most-used destinations come first.
 */
export const NAV_BY_ROLE: Record<Role, NavDef[]> = {
  student: [
    { to: "/s", label: "今日任务", icon: "ui-home", end: true },
    { to: "/s/recordings", label: "我的录音", icon: "ui-list", end: true },
    { to: "/s/me", label: "我的", icon: "ui-user", end: true },
  ],
  annotator: [
    { to: "/a", label: "标注队列", icon: "ui-inbox", end: true },
    { to: "/a/rereview", label: "复评", icon: "ui-swap", end: true },
    { to: "/a/me", label: "我的进度", icon: "ui-chart-line", end: true },
  ],
  adjudicator: [{ to: "/j", label: "仲裁", icon: "ui-shield", end: true }],
  // 班级教师 (ACCOUNTS.md §5) — class oversight, no export or account admin.
  // Teachers publish batches and mass-dispatch to their own class.
  teacher: [
    { to: "/m", label: "总览", icon: "ui-home", end: true },
    { to: "/m/classes", label: "班级", icon: "ui-school" },
    { to: "/m/batches", label: "发布任务", icon: "ui-folder" },
    { to: "/m/speakers", label: "我的学生", icon: "ui-users" },
    { to: "/m/qc", label: "质检", icon: "ui-check-circle" },
    { to: "/m/stimuli", label: "示范录音", icon: "ui-microphone" },
  ],
  course_team: [
    { to: "/m", label: "总览", icon: "ui-home", end: true },
    { to: "/m/classes", label: "班级", icon: "ui-school" },
    { to: "/m/materials", label: "素材", icon: "ui-document" },
    { to: "/m/speakers", label: "说话人", icon: "ui-users" },
    { to: "/m/batches", label: "批次", icon: "ui-folder" },
    { to: "/m/qc", label: "质检", icon: "ui-check-circle" },
    { to: "/m/quality", label: "质量", icon: "ui-chart-line" },
    { to: "/m/stimuli", label: "示范录音", icon: "ui-microphone" },
    { to: "/m/export", label: "导出", icon: "ui-download" },
  ],
  admin: [
    { to: "/m", label: "总览", icon: "ui-home", end: true },
    { to: "/m/classes", label: "班级", icon: "ui-school" },
    { to: "/m/materials", label: "素材", icon: "ui-document" },
    { to: "/m/speakers", label: "说话人", icon: "ui-users" },
    { to: "/m/batches", label: "批次", icon: "ui-folder" },
    { to: "/m/qc", label: "质检", icon: "ui-check-circle" },
    { to: "/m/quality", label: "质量", icon: "ui-chart-line" },
    { to: "/m/stimuli", label: "示范录音", icon: "ui-microphone" },
    { to: "/m/export", label: "导出", icon: "ui-download" },
    { to: "/m/users", label: "账号", icon: "ui-key" },
  ],
};

/** Admin nav gains 「AI 评分」 only while the feature flag is on. */
export function navForRole(role: Role | null, aiEnabled: boolean): NavDef[] {
  const base = role ? NAV_BY_ROLE[role] : [];
  if (role !== "admin" || !aiEnabled) return base;
  const item: NavDef = { to: "/m/ai", label: "AI 评分", icon: "ui-grade", end: true };
  const users = base.findIndex((d) => d.to === "/m/users");
  if (users < 0) return [...base, item];
  return [...base.slice(0, users), item, ...base.slice(users)];
}

const renderLink: RenderLink = ({ to, className, children, key, ...rest }) => (
  <NavLink key={key} to={to} className={className} {...rest}>
    {children}
  </NavLink>
);

function isActive(pathname: string, item: NavDef): boolean {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Application frame: role-aware navigation, role switcher for multi-role
 * accounts, theme switch and the offline strip. Feature routes render into
 * the `<Outlet/>`.
 */
export function AppShell() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const roles = sortRoles(me?.user.roles ?? []);
  const [activeRole, setActiveRole] = useActiveRole(roles);
  const expert = roles.some((r) => r === "annotator" || r === "adjudicator");
  const { theme, cycle } = useTheme();
  const location = useLocation();
  const online = useOnline();
  // docs/ROADMAP.md B2 — the offline upload queue follows the session.
  useUploadQueueBridge();
  const queue = useUploadQueue();
  const queued = pendingCount(queue);

  const aiOn = useAiEnabled();
  const defs = useMemo(() => navForRole(activeRole, aiOn), [activeRole, aiOn]);
  const nav: NavItem[] = defs.map((d) => ({
    to: d.to,
    label: d.label,
    icon: <Icon id={d.icon} />,
    active: isActive(location.pathname, d),
  }));

  const themeIcon: IconId =
    theme === "dark" ? "ui-moon" : theme === "light" ? "ui-sun" : "ui-settings";

  return (
    <UiAppShell
      nav={nav}
      renderLink={renderLink}
      theme={theme}
      title="星海正音"
      roleSwitcher={
        roles.length > 1 ? (
          <label className="xz-rolepick">
            <span className="xz-sr-only">切换身份</span>
            <Select
              value={activeRole ?? ""}
              aria-label="切换身份"
              options={roles.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
              onChange={(e) => {
                const role = e.target.value as Role;
                setActiveRole(role);
                void navigate(homeFor(role));
              }}
            />
          </label>
        ) : activeRole ? (
          <span className="gz-caption xz-rolepick__single">
            {ROLE_LABELS[activeRole]}
            {expert ? " · 专家" : ""}
          </span>
        ) : null
      }
      headerActions={
        <>
          <IconButton
            icon={<Icon id={themeIcon} />}
            label={`外观：${THEME_LABELS[theme]}，点击切换`}
            onClick={cycle}
          />
          <AccountMenu />
        </>
      }
      banner={
        online ? (
          queued > 0 ? (
            <span className="xz-offline">
              <Icon id="ui-upload" size={16} />
              还有 {queued} 条录音在补传，可以继续用。
            </span>
          ) : null
        ) : (
          <span className="xz-offline">
            <Icon id="ui-info" size={16} />
            当前没有网络连接。你可以照常录音，录好的会先存在手机里，联网后自动补传。
          </span>
        )
      }
    >
      <ViewAsBanner />
      <RecoveryNudge />
      <Outlet />
    </UiAppShell>
  );
}

const RECOVERY_DISMISS = "xz.recovery_prompt_dismissed";

/** Stays up for the whole viewing session. Leaving is the only action on it. */
function ViewAsBanner() {
  const { me, refetch } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const viewing = me?.viewing_as;
  const end = useMutation({
    mutationFn: () => usersApi.endViewAs(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/m");
    },
    onError: (err) => toast.error(friendlyMessage(err, "没能退出查看。")),
  });

  useEffect(() => {
    if (!viewing) return;
    document.documentElement.classList.add("xz-viewing-as");
    const left = Date.parse(viewing.expires_at) - Date.now();
    const timer = window.setTimeout(
      () => {
        void refetch();
        navigate("/m");
      },
      Math.max(0, left) + 250,
    );
    return () => {
      window.clearTimeout(timer);
      document.documentElement.classList.remove("xz-viewing-as");
    };
  }, [navigate, refetch, viewing]);

  if (!viewing) return null;
  return (
    <p className="xz-viewas" role="status" aria-label="以学生身份查看">
      正在以 {viewing.display_name} 身份查看 ·{" "}
      <button
        type="button"
        className="gz-button gz-button--ghost"
        onClick={() => end.mutate()}
      >
        退出
      </button>
    </p>
  );
}

/** Gentle, dismissible reminder to bind an email or a phone. */
function RecoveryNudge() {
  const { me } = useAuth();
  const [gone, setGone] = useState(false);
  let dismissed = false;
  try {
    dismissed = localStorage.getItem(RECOVERY_DISMISS) === "1";
  } catch {
    dismissed = false;
  }
  const recovery = me?.recovery;
  if (!me || !recovery || gone || dismissed) return null;
  if (recovery.email_verified || recovery.phone_verified) return null;
  return (
    <p className="gz-notice xz-container" role="status">
      建议绑定邮箱或手机，方便自己找回密码。
      <Link to="/account/email">绑定邮箱</Link>
      {" · "}
      <Link to="/account/phone">绑定手机</Link>
      <button
        type="button"
        className="gz-button gz-button--ghost"
        onClick={() => {
          try {
            localStorage.setItem(RECOVERY_DISMISS, "1");
          } catch {
            /* ignore */
          }
          setGone(true);
        }}
      >
        以后再说
      </button>
    </p>
  );
}
